import { useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FieldWithValue, FormValuesType } from 'src/UI/dynamicUI/types';
import {
  FormAccordion,
  FormAccordionItem,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Divider, Grid } from '@mantine/core';
import { useParams, useSearchParams } from 'react-router';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
  updateFeedInfoAfterAssetCreation,
  validateFundHoldingsAndDisplayError,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  FUND_HOLDING,
  FundHoldingFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/FundHoldingFormAccordionItemList';
import { ContributionFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/ContributionFormAccordionItemList';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import { FeeFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/FeeFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import {
  FormTitleWithBadges,
  useConnectedDataIcon,
  useCurrencyConversionIcon,
} from 'src/financial/financial-data/financial-item/forms/common/ConnectedDataBadge';
import {
  useValidateProviderReference,
  validateProviderReferenceAndSetError,
} from 'src/financial/financial-data/hooks/useValidateProviderReference';
import {
  scrollToElementWithError,
  validateContributionEndDate,
} from 'src/financial/financial-data/financial-item/forms/common/validationUtils';

const DEFINED_CONTRIBUTION_PRODUCT = 'DefinedContributionPension';
const CONTRIBUTION = 'DcPensionContribution';
const FEE_TYPE = 'InvestmentAndPensionFee';
export function DefinedContributionPension(props: ProductFormComponentProps) {
  const { onSave } = props;
  const [searchParams] = useSearchParams();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { validateProviderReference } = useValidateProviderReference(clientId);

  const {
    isCreateMode,
    uiData,
    initialValues,
    onDeleteConfirm,
    feedInfo: initialFeedInfo,
    onConnectAsset,
  } = getFinancialFormSetupValues(DEFINED_CONTRIBUTION_PRODUCT, props, [
    { product: FUND_HOLDING, field: 'fund_holdings' },
    { product: CONTRIBUTION, field: 'contributions' },
    { product: BENEFICIARY, field: 'beneficiaries' },
    { product: FEE_TYPE, field: 'fees' },
  ]);

  const [feedInfo, setFeedInfo] = useState<FeedInfo>(initialFeedInfo);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
    validate: {
      [CONTRIBUTION]: {
        end_date: (value, values, path) => {
          return validateContributionEndDate(CONTRIBUTION, value, values, path);
        },
      },
    },
  });

  useEffect(() => {
    const providerReference = searchParams.get('providerReference');
    if (providerReference && isCreateMode) {
      form.setFieldValue(
        `${DEFINED_CONTRIBUTION_PRODUCT}.provider_reference_number`,
        providerReference,
      );
    }
    const policyNumber = searchParams.get('policyNumber');
    if (policyNumber && isCreateMode) {
      form.setFieldValue(`${DEFINED_CONTRIBUTION_PRODUCT}.policy_number`, policyNumber);
    }
  }, [searchParams, isCreateMode, form]);

  const handleSave = async (values: FormValuesType) => {
    if (
      FUND_HOLDING in values &&
      Array.isArray(values[FUND_HOLDING]) &&
      CONTRIBUTION in values &&
      Array.isArray(values[CONTRIBUTION]) &&
      BENEFICIARY in values &&
      Array.isArray(values[BENEFICIARY]) &&
      FEE_TYPE in values &&
      Array.isArray(values[FEE_TYPE])
    ) {
      const pensionData = values[DEFINED_CONTRIBUTION_PRODUCT] as Record<string, unknown>;

      const isProviderReferenceValid = await validateProviderReferenceAndSetError(
        pensionData,
        clientId,
        form,
        DEFINED_CONTRIBUTION_PRODUCT,
        validateProviderReference,
      );
      if (!isProviderReferenceValid) {
        return;
      }

      if (!validateFundHoldingsAndDisplayError(values, form)) {
        return;
      }

      const mappedValues = {
        ...pensionData,
        fund_holdings: values[FUND_HOLDING].map(item => ({ ...item })),
        contributions: values[CONTRIBUTION].map(item => ({ ...item })),
        beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
        fees: values[FEE_TYPE].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[DEFINED_CONTRIBUTION_PRODUCT]);

      updateFeedInfoAfterAssetCreation(
        form,
        DEFINED_CONTRIBUTION_PRODUCT,
        isCreateMode,
        setFeedInfo,
      );
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave, scrollToElementWithError)} autoComplete={'off'}>
        <FormContent form={form} feedInfo={feedInfo} onConnect={onConnectAsset} />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({ form, feedInfo, onConnect }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const connectedDataIcon = useConnectedDataIcon(feedInfo.isUpdatedAutomatically);

  const fundHoldings = form.getValues()[FUND_HOLDING];
  const hasCurrencyConversions =
    Array.isArray(fundHoldings) &&
    fundHoldings.some(
      (holding: { original_currency_code?: string | null }) => !!holding.original_currency_code,
    );
  const currencyConversionIcon = useCurrencyConversionIcon(hasCurrencyConversions);

  const productValues = form.getValues()[DEFINED_CONTRIBUTION_PRODUCT] as FieldWithValue;

  const summaryContent = (
    <Grid>
      <GridPair
        first={<FinancialFormField field="type" product={DEFINED_CONTRIBUTION_PRODUCT} />}
        second={<FinancialFormField field="description" product={DEFINED_CONTRIBUTION_PRODUCT} />}
      />
      <GridPair
        isHighlighted
        first={<FinancialFormField field="provider" product={DEFINED_CONTRIBUTION_PRODUCT} />}
        second={
          <FinancialFormField
            field="provider_reference_number"
            product={DEFINED_CONTRIBUTION_PRODUCT}
          />
        }
      />
      <GridPair
        first={<FinancialFormField field="policy_number" product={DEFINED_CONTRIBUTION_PRODUCT} />}
      />
      <GridPair
        first={
          <FinancialFormField
            field="start_date"
            product={DEFINED_CONTRIBUTION_PRODUCT}
            forceEditable
          />
        }
        second={
          <FinancialFormField field="status" product={DEFINED_CONTRIBUTION_PRODUCT} forceEditable />
        }
      />
      <GridPair
        isHighlighted
        first={<FinancialFormField field="value" product={DEFINED_CONTRIBUTION_PRODUCT} />}
        second={<FinancialFormField field="value_date" product={DEFINED_CONTRIBUTION_PRODUCT} />}
      />
      <GridPair
        first={
          <FinancialFormField
            field="is_serviced_by_advisor"
            product={DEFINED_CONTRIBUTION_PRODUCT}
            forceEditable
          />
        }
      />
    </Grid>
  );

  const [isProtectedTfcApplies, setIsProtectedTfcApplies] = useState(
    productValues['does_protected_tfc_apply'] === 'true',
  );

  const [isAnnuityRateGuaranteed, setIsAnnuityRateGuaranteed] = useState(
    productValues['is_annuity_rate_guaranteed'] === 'true',
  );

  form.watch(`${DEFINED_CONTRIBUTION_PRODUCT}.does_protected_tfc_apply`, ({ value }) => {
    setIsProtectedTfcApplies(value === 'true');
  });

  form.watch(`${DEFINED_CONTRIBUTION_PRODUCT}.is_annuity_rate_guaranteed`, ({ value }) => {
    setIsAnnuityRateGuaranteed(value === 'true');
  });

  const titleText = 'Assets > Pensions > Defined contribution';
  const titleWithBadges = (
    <FormTitleWithBadges
      title={titleText}
      showConnectedBadge={feedInfo.isUpdatedAutomatically}
      showCurrencyBadge={hasCurrencyConversions}
    />
  );

  return (
    <FinancialFormContainer title={titleWithBadges} feedInfo={feedInfo} onConnect={onConnect}>
      <FormAccordion
        defaultValue={[
          'summary',
          'fund_holdings',
          'contribution',
          'beneficiaries',
          'fee',
          'important',
        ]}
      >
        <FormAccordionItem value="summary" label="Summary" titleIcon={connectedDataIcon}>
          {feedInfo.isUpdatedAutomatically ? (
            <ReadOnlyWrapper>{summaryContent}</ReadOnlyWrapper>
          ) : (
            summaryContent
          )}
        </FormAccordionItem>
        <ContributionFormAccordionItemList
          form={form}
          isEditing={isEditing}
          contributionType={CONTRIBUTION}
        />
        <FormAccordionItem value="important" label="Important information">
          <Grid>
            <GridPair
              first={
                <FinancialFormField
                  field="retirement_option"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={
                <FinancialFormField
                  field="benefit_crystallisation_status"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
            />
          </Grid>
          <Divider my="md" />
          <Grid>
            <GridPair
              first={
                <FinancialFormField
                  field="does_protected_tfc_apply"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={
                <FinancialFormField
                  field="protected_tfc_amount"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                  hide={!isProtectedTfcApplies}
                />
              }
            />
            <GridPair
              hide={!isProtectedTfcApplies}
              first={
                <FinancialFormField
                  field="protected_pension_age"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={
                <FinancialFormField
                  field="is_protected_tfc_transferable"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
            />
          </Grid>
          <Grid>
            <GridPair
              hide={!isProtectedTfcApplies}
              first={
                <FinancialFormField
                  field="was_loss_of_transfer_warning_given"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={
                <FinancialFormField
                  field="is_preserved_post_transfer"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
            />
          </Grid>
          <Divider my="md" />
          <Grid>
            <GridPair
              first={
                <FinancialFormField
                  field="is_annuity_rate_guaranteed"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={
                <FinancialFormField
                  hide={!isAnnuityRateGuaranteed}
                  field="gar_percentage"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
            />
            <GridPair
              first={
                <FinancialFormField
                  hide={!isAnnuityRateGuaranteed}
                  field="guaranteed_minimum_pension"
                  product={DEFINED_CONTRIBUTION_PRODUCT}
                />
              }
              second={null}
            />
          </Grid>
        </FormAccordionItem>
        <FeeFormAccordionItemList form={form} isEditing={isEditing} feeType={FEE_TYPE} />
        <FundHoldingFormAccordionItemList
          form={form}
          isEditing={isEditing}
          productType={DEFINED_CONTRIBUTION_PRODUCT}
          titleIcon={connectedDataIcon}
          currencyIcon={currencyConversionIcon}
          feedInfo={feedInfo}
        />
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};
